import React, { useState, useCallback } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Type,
  AlignLeft,
  Image,
  PlayCircle,
  MousePointerClick,
  Code,
  Smile,
  Share2,
  Minus,
  ShoppingBag,
  Navigation,
  Square,
  GripVertical,
  Trash2,
  Settings,
  Eye,
  Save,
  X,
  ChevronDown,
  ChevronUp,
  Link,
  Palette,
  AlignCenter,
  AlignRight,
  Bold,
  Italic,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

// Block types definition
const BLOCK_TYPES = [
  { type: "title", name: "Titre", icon: Type, color: "#0D9488" },
  { type: "text", name: "Texte", icon: AlignLeft, color: "#0D9488" },
  { type: "image", name: "Image", icon: Image, color: "#0D9488" },
  { type: "button", name: "Bouton", icon: MousePointerClick, color: "#0D9488" },
  { type: "divider", name: "Séparateur", icon: Minus, color: "#0D9488" },
  { type: "spacer", name: "Espacement", icon: Square, color: "#0D9488" },
  { type: "social", name: "Réseaux sociaux", icon: Share2, color: "#0D9488" },
  { type: "html", name: "HTML", icon: Code, color: "#0D9488" },
];

// Pre-built sections
const SECTIONS = [
  {
    id: "hero-1",
    name: "Hero avec image",
    category: "Textes et images",
    thumbnail: "https://placehold.co/400x200/0D9488/white?text=Hero",
    blocks: [
      { type: "image", content: { src: "https://placehold.co/600x300/0D9488/white?text=Image", alt: "Hero image" } },
      { type: "title", content: { text: "Titre principal", align: "center" } },
      { type: "text", content: { text: "Lorem ipsum dolor sit amet, consectetur adipiscing elit.", align: "center" } },
      { type: "button", content: { text: "En savoir plus", url: "#", align: "center", bgColor: "#333333" } },
    ],
  },
  {
    id: "hero-2",
    name: "Hero avec overlay",
    category: "Textes et images",
    thumbnail: "https://placehold.co/400x200/1e293b/white?text=Hero+Overlay",
    blocks: [
      { type: "image", content: { src: "https://placehold.co/600x400/1e293b/white?text=Background", alt: "Background" } },
      { type: "title", content: { text: "Titre sur image", align: "center", color: "#ffffff" } },
      { type: "text", content: { text: "Description sur fond sombre", align: "center", color: "#ffffff" } },
      { type: "button", content: { text: "Découvrir", url: "#", align: "center", bgColor: "#ffffff", textColor: "#000000" } },
    ],
  },
  {
    id: "product-1",
    name: "Produit classique",
    category: "Produits",
    thumbnail: "https://placehold.co/400x200/f59e0b/white?text=Produit",
    blocks: [
      { type: "image", content: { src: "https://placehold.co/300x300/f59e0b/white?text=Produit", alt: "Produit" } },
      { type: "title", content: { text: "Nom du produit", align: "center", fontSize: "24px" } },
      { type: "text", content: { text: "À partir de 29€", align: "center", color: "#f59e0b" } },
      { type: "text", content: { text: "Description du produit avec ses caractéristiques principales.", align: "center" } },
      { type: "button", content: { text: "Commander", url: "#", align: "center", bgColor: "#f59e0b" } },
    ],
  },
  {
    id: "cta-1",
    name: "Call to action",
    category: "Actions",
    thumbnail: "https://placehold.co/400x150/8B5CF6/white?text=CTA",
    blocks: [
      { type: "title", content: { text: "Prêt à commencer ?", align: "center" } },
      { type: "text", content: { text: "Contactez-nous dès maintenant pour en savoir plus.", align: "center" } },
      { type: "button", content: { text: "Nous contacter", url: "#", align: "center", bgColor: "#8B5CF6" } },
    ],
  },
  {
    id: "footer-1",
    name: "Footer avec réseaux",
    category: "Footer",
    thumbnail: "https://placehold.co/400x100/64748b/white?text=Footer",
    blocks: [
      { type: "divider", content: { color: "#e5e5e5", height: "1px" } },
      { type: "social", content: { facebook: "#", instagram: "#", linkedin: "#" } },
      { type: "text", content: { text: "© 2026 Food Truck United - Tous droits réservés", align: "center", fontSize: "12px", color: "#666666" } },
    ],
  },
];

// Default content for each block type
const getDefaultContent = (type) => {
  switch (type) {
    case "title":
      return { text: "Nouveau titre", align: "center", fontSize: "32px", color: "#333333", fontWeight: "bold" };
    case "text":
      return { text: "Votre texte ici. Cliquez pour éditer.", align: "left", fontSize: "16px", color: "#333333" };
    case "image":
      return { src: "https://placehold.co/600x300/0D9488/white?text=Image", alt: "Image", width: "100%", align: "center" };
    case "button":
      return { text: "Cliquez ici", url: "#", align: "center", bgColor: "#0D9488", textColor: "#ffffff", borderRadius: "4px" };
    case "divider":
      return { color: "#e5e5e5", height: "1px", margin: "20px" };
    case "spacer":
      return { height: "30px" };
    case "social":
      return { facebook: "", instagram: "", linkedin: "", twitter: "" };
    case "html":
      return { code: "<p>Code HTML personnalisé</p>" };
    default:
      return {};
  }
};

// Draggable Block Component from Sidebar
const DraggableBlock = ({ type, name, icon: Icon }) => {
  const [isDragging, setIsDragging] = useState(false);

  const handleDragStart = (e) => {
    e.dataTransfer.setData("blockType", type);
    e.dataTransfer.setData("isNew", "true");
    setIsDragging(true);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      className={`flex flex-col items-center justify-center p-4 border-2 border-gray-200 rounded-lg cursor-grab hover:border-primary hover:bg-primary/5 transition-all ${
        isDragging ? "opacity-50 border-primary" : ""
      }`}
    >
      <div className="flex items-center justify-center mb-2">
        <span className="text-xs text-gray-400 tracking-widest">····</span>
      </div>
      <div className="p-3 rounded-lg border border-primary/30 bg-white">
        <Icon className="w-6 h-6 text-primary" />
      </div>
      <span className="text-sm font-medium text-gray-700 mt-2">{name}</span>
    </div>
  );
};

// Section Card Component
const SectionCard = ({ section, onAdd }) => {
  return (
    <div
      className="border rounded-lg overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
      onClick={() => onAdd(section)}
    >
      <div className="flex items-center justify-center bg-gray-50 p-2">
        <span className="text-xs text-gray-400 tracking-widest">····</span>
      </div>
      <img
        src={section.thumbnail}
        alt={section.name}
        className="w-full h-32 object-cover"
      />
      <div className="p-2 text-center">
        <p className="text-sm font-medium">{section.name}</p>
      </div>
    </div>
  );
};

// Sortable Canvas Block
const SortableBlock = ({ block, index, onUpdate, onDelete, isSelected, onSelect }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const renderBlockContent = () => {
    const content = block.content || {};
    
    switch (block.type) {
      case "title":
        return (
          <h2
            style={{
              textAlign: content.align || "center",
              fontSize: content.fontSize || "32px",
              color: content.color || "#333333",
              fontWeight: content.fontWeight || "bold",
              margin: 0,
            }}
          >
            {content.text || "Titre"}
          </h2>
        );
      case "text":
        return (
          <p
            style={{
              textAlign: content.align || "left",
              fontSize: content.fontSize || "16px",
              color: content.color || "#333333",
              margin: 0,
              lineHeight: 1.6,
            }}
          >
            {content.text || "Texte"}
          </p>
        );
      case "image":
        return (
          <div style={{ textAlign: content.align || "center" }}>
            <img
              src={content.src || "https://placehold.co/600x300"}
              alt={content.alt || "Image"}
              style={{ maxWidth: content.width || "100%", height: "auto", borderRadius: "4px" }}
            />
          </div>
        );
      case "button":
        return (
          <div style={{ textAlign: content.align || "center" }}>
            <button
              style={{
                backgroundColor: content.bgColor || "#0D9488",
                color: content.textColor || "#ffffff",
                padding: "12px 24px",
                border: "none",
                borderRadius: content.borderRadius || "4px",
                cursor: "pointer",
                fontSize: "16px",
                fontWeight: "500",
              }}
            >
              {content.text || "Bouton"}
            </button>
          </div>
        );
      case "divider":
        return (
          <hr
            style={{
              border: "none",
              borderTop: `${content.height || "1px"} solid ${content.color || "#e5e5e5"}`,
              margin: `${content.margin || "20px"} 0`,
            }}
          />
        );
      case "spacer":
        return <div style={{ height: content.height || "30px" }} />;
      case "social":
        return (
          <div style={{ textAlign: "center" }}>
            <div style={{ display: "inline-flex", gap: "16px" }}>
              {content.facebook && (
                <a href={content.facebook} style={{ color: "#1877f2" }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
                  </svg>
                </a>
              )}
              {content.instagram && (
                <a href={content.instagram} style={{ color: "#e4405f" }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="2" y="2" width="20" height="20" rx="5"/>
                    <circle cx="12" cy="12" r="4"/>
                    <circle cx="18" cy="6" r="1"/>
                  </svg>
                </a>
              )}
              {content.linkedin && (
                <a href={content.linkedin} style={{ color: "#0077b5" }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/>
                    <rect x="2" y="9" width="4" height="12"/>
                    <circle cx="4" cy="4" r="2"/>
                  </svg>
                </a>
              )}
            </div>
          </div>
        );
      case "html":
        return <iframe title="Aperçu" sandbox="allow-same-origin" srcDoc={content.code || ""} style={{ width: "100%", border: 0, minHeight: 80 }} onLoad={(e) => { try { e.target.style.height = (e.target.contentDocument.documentElement.scrollHeight + 4) + "px"; } catch (_) {} }} />;
      default:
        return <div>Bloc inconnu</div>;
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group relative bg-white border-2 rounded-lg p-4 mb-3 transition-all ${
        isSelected ? "border-primary shadow-lg" : "border-transparent hover:border-gray-300"
      }`}
      onClick={() => onSelect(block.id)}
    >
      {/* Drag handle */}
      <div
        {...attributes}
        {...listeners}
        className="absolute left-0 top-0 bottom-0 w-8 flex items-center justify-center cursor-grab opacity-0 group-hover:opacity-100 transition-opacity bg-gray-100 rounded-l-lg"
      >
        <GripVertical className="w-4 h-4 text-gray-400" />
      </div>
      
      {/* Delete button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDelete(block.id);
        }}
        className="absolute right-2 top-2 p-1 rounded bg-red-100 text-red-600 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-200"
      >
        <Trash2 className="w-4 h-4" />
      </button>
      
      {/* Block content */}
      <div className="pl-6">
        {renderBlockContent()}
      </div>
    </div>
  );
};

// Block Editor Panel
const BlockEditor = ({ block, onUpdate, onClose }) => {
  if (!block) return null;

  const content = block.content || {};

  const updateContent = (key, value) => {
    onUpdate(block.id, { ...content, [key]: value });
  };

  const renderEditor = () => {
    switch (block.type) {
      case "title":
      case "text":
        return (
          <div className="space-y-4">
            <div>
              <Label>Contenu</Label>
              {block.type === "title" ? (
                <Input
                  value={content.text || ""}
                  onChange={(e) => updateContent("text", e.target.value)}
                  className="mt-1"
                />
              ) : (
                <Textarea
                  value={content.text || ""}
                  onChange={(e) => updateContent("text", e.target.value)}
                  className="mt-1"
                  rows={4}
                />
              )}
            </div>
            <div>
              <Label>Alignement</Label>
              <div className="flex gap-2 mt-1">
                <Button
                  variant={content.align === "left" ? "default" : "outline"}
                  size="sm"
                  onClick={() => updateContent("align", "left")}
                >
                  <AlignLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant={content.align === "center" ? "default" : "outline"}
                  size="sm"
                  onClick={() => updateContent("align", "center")}
                >
                  <AlignCenter className="w-4 h-4" />
                </Button>
                <Button
                  variant={content.align === "right" ? "default" : "outline"}
                  size="sm"
                  onClick={() => updateContent("align", "right")}
                >
                  <AlignRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Taille</Label>
                <Select value={content.fontSize || "16px"} onValueChange={(v) => updateContent("fontSize", v)}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="12px">12px</SelectItem>
                    <SelectItem value="14px">14px</SelectItem>
                    <SelectItem value="16px">16px</SelectItem>
                    <SelectItem value="18px">18px</SelectItem>
                    <SelectItem value="24px">24px</SelectItem>
                    <SelectItem value="32px">32px</SelectItem>
                    <SelectItem value="48px">48px</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Couleur</Label>
                <Input
                  type="color"
                  value={content.color || "#333333"}
                  onChange={(e) => updateContent("color", e.target.value)}
                  className="mt-1 h-10 p-1"
                />
              </div>
            </div>
          </div>
        );
      
      case "image":
        return (
          <div className="space-y-4">
            <div>
              <Label>URL de l'image</Label>
              <Input
                value={content.src || ""}
                onChange={(e) => updateContent("src", e.target.value)}
                className="mt-1"
                placeholder="https://..."
              />
            </div>
            <div>
              <Label>Texte alternatif</Label>
              <Input
                value={content.alt || ""}
                onChange={(e) => updateContent("alt", e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Largeur</Label>
              <Select value={content.width || "100%"} onValueChange={(v) => updateContent("width", v)}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="100%">100%</SelectItem>
                  <SelectItem value="75%">75%</SelectItem>
                  <SelectItem value="50%">50%</SelectItem>
                  <SelectItem value="300px">300px</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );
      
      case "button":
        return (
          <div className="space-y-4">
            <div>
              <Label>Texte du bouton</Label>
              <Input
                value={content.text || ""}
                onChange={(e) => updateContent("text", e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label>URL de destination</Label>
              <Input
                value={content.url || ""}
                onChange={(e) => updateContent("url", e.target.value)}
                className="mt-1"
                placeholder="https://..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Couleur fond</Label>
                <Input
                  type="color"
                  value={content.bgColor || "#0D9488"}
                  onChange={(e) => updateContent("bgColor", e.target.value)}
                  className="mt-1 h-10 p-1"
                />
              </div>
              <div>
                <Label>Couleur texte</Label>
                <Input
                  type="color"
                  value={content.textColor || "#ffffff"}
                  onChange={(e) => updateContent("textColor", e.target.value)}
                  className="mt-1 h-10 p-1"
                />
              </div>
            </div>
            <div>
              <Label>Alignement</Label>
              <div className="flex gap-2 mt-1">
                <Button
                  variant={content.align === "left" ? "default" : "outline"}
                  size="sm"
                  onClick={() => updateContent("align", "left")}
                >
                  <AlignLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant={content.align === "center" ? "default" : "outline"}
                  size="sm"
                  onClick={() => updateContent("align", "center")}
                >
                  <AlignCenter className="w-4 h-4" />
                </Button>
                <Button
                  variant={content.align === "right" ? "default" : "outline"}
                  size="sm"
                  onClick={() => updateContent("align", "right")}
                >
                  <AlignRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        );
      
      case "divider":
        return (
          <div className="space-y-4">
            <div>
              <Label>Couleur</Label>
              <Input
                type="color"
                value={content.color || "#e5e5e5"}
                onChange={(e) => updateContent("color", e.target.value)}
                className="mt-1 h-10 p-1"
              />
            </div>
            <div>
              <Label>Épaisseur</Label>
              <Select value={content.height || "1px"} onValueChange={(v) => updateContent("height", v)}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1px">1px</SelectItem>
                  <SelectItem value="2px">2px</SelectItem>
                  <SelectItem value="3px">3px</SelectItem>
                  <SelectItem value="5px">5px</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );
      
      case "spacer":
        return (
          <div>
            <Label>Hauteur</Label>
            <Select value={content.height || "30px"} onValueChange={(v) => updateContent("height", v)}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10px">10px</SelectItem>
                <SelectItem value="20px">20px</SelectItem>
                <SelectItem value="30px">30px</SelectItem>
                <SelectItem value="50px">50px</SelectItem>
                <SelectItem value="80px">80px</SelectItem>
              </SelectContent>
            </Select>
          </div>
        );
      
      case "social":
        return (
          <div className="space-y-4">
            <div>
              <Label>Facebook</Label>
              <Input
                value={content.facebook || ""}
                onChange={(e) => updateContent("facebook", e.target.value)}
                className="mt-1"
                placeholder="https://facebook.com/..."
              />
            </div>
            <div>
              <Label>Instagram</Label>
              <Input
                value={content.instagram || ""}
                onChange={(e) => updateContent("instagram", e.target.value)}
                className="mt-1"
                placeholder="https://instagram.com/..."
              />
            </div>
            <div>
              <Label>LinkedIn</Label>
              <Input
                value={content.linkedin || ""}
                onChange={(e) => updateContent("linkedin", e.target.value)}
                className="mt-1"
                placeholder="https://linkedin.com/..."
              />
            </div>
          </div>
        );
      
      case "html":
        return (
          <div>
            <Label>Code HTML</Label>
            <Textarea
              value={content.code || ""}
              onChange={(e) => updateContent("code", e.target.value)}
              className="mt-1 font-mono text-sm"
              rows={8}
              placeholder="<p>Votre code HTML</p>"
            />
          </div>
        );
      
      default:
        return <p>Éditeur non disponible pour ce type de bloc</p>;
    }
  };

  return (
    <div className="p-4 border-l bg-white h-full overflow-y-auto">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold flex items-center gap-2">
          <Settings className="w-4 h-4" />
          Éditer {block.type}
        </h3>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X className="w-4 h-4" />
        </Button>
      </div>
      {renderEditor()}
    </div>
  );
};

// Main Editor Component
export default function EmailTemplateEditor({ onSave, onCancel, initialBlocks = [], initialSubject = "" }) {
  const [blocks, setBlocks] = useState(initialBlocks);
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [sidebarTab, setSidebarTab] = useState("blocs");
  const [templateName, setTemplateName] = useState("");
  const [templateSubject, setTemplateSubject] = useState(initialSubject);
  const [showPreview, setShowPreview] = useState(false);
  const [sectionCategory, setSectionCategory] = useState("all");

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const selectedBlock = blocks.find((b) => b.id === selectedBlockId);

  // Handle drop from sidebar
  const handleCanvasDrop = (e) => {
    e.preventDefault();
    const blockType = e.dataTransfer.getData("blockType");
    const isNew = e.dataTransfer.getData("isNew");

    if (isNew === "true" && blockType) {
      const newBlock = {
        id: `block-${Date.now()}`,
        type: blockType,
        content: getDefaultContent(blockType),
      };
      setBlocks([...blocks, newBlock]);
      setSelectedBlockId(newBlock.id);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  // Handle reorder within canvas
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setBlocks((items) => {
        const oldIndex = items.findIndex((i) => i.id === active.id);
        const newIndex = items.findIndex((i) => i.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  // Update block content
  const updateBlock = (blockId, newContent) => {
    setBlocks((prev) =>
      prev.map((b) => (b.id === blockId ? { ...b, content: newContent } : b))
    );
  };

  // Delete block
  const deleteBlock = (blockId) => {
    setBlocks((prev) => prev.filter((b) => b.id !== blockId));
    if (selectedBlockId === blockId) {
      setSelectedBlockId(null);
    }
  };

  // Add section
  const addSection = (section) => {
    const newBlocks = section.blocks.map((block, index) => ({
      id: `block-${Date.now()}-${index}`,
      type: block.type,
      content: block.content,
    }));
    setBlocks([...blocks, ...newBlocks]);
  };

  // Generate HTML output
  const generateHTML = () => {
    let html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${templateSubject}</title>
</head>
<body style="margin: 0; padding: 0; font-family: Arial, sans-serif; background-color: #f5f5f5;">
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background-color: #f5f5f5;">
    <tr>
      <td align="center" style="padding: 40px 0;">
        <table role="presentation" width="600" cellspacing="0" cellpadding="0" style="background-color: #ffffff; border-radius: 8px; overflow: hidden;">
          <tr>
            <td style="padding: 30px;">
`;

    blocks.forEach((block) => {
      const content = block.content || {};
      switch (block.type) {
        case "title":
          html += `<h2 style="text-align: ${content.align || "center"}; font-size: ${content.fontSize || "32px"}; color: ${content.color || "#333333"}; font-weight: ${content.fontWeight || "bold"}; margin: 0 0 20px 0;">${content.text || ""}</h2>\n`;
          break;
        case "text":
          html += `<p style="text-align: ${content.align || "left"}; font-size: ${content.fontSize || "16px"}; color: ${content.color || "#333333"}; line-height: 1.6; margin: 0 0 20px 0;">${content.text || ""}</p>\n`;
          break;
        case "image":
          html += `<div style="text-align: ${content.align || "center"}; margin: 20px 0;"><img src="${content.src || ""}" alt="${content.alt || ""}" style="max-width: ${content.width || "100%"}; height: auto; border-radius: 4px;" /></div>\n`;
          break;
        case "button":
          html += `<div style="text-align: ${content.align || "center"}; margin: 20px 0;"><a href="${content.url || "#"}" style="display: inline-block; background-color: ${content.bgColor || "#0D9488"}; color: ${content.textColor || "#ffffff"}; padding: 12px 24px; text-decoration: none; border-radius: ${content.borderRadius || "4px"}; font-size: 16px; font-weight: 500;">${content.text || "Bouton"}</a></div>\n`;
          break;
        case "divider":
          html += `<hr style="border: none; border-top: ${content.height || "1px"} solid ${content.color || "#e5e5e5"}; margin: ${content.margin || "20px"} 0;" />\n`;
          break;
        case "spacer":
          html += `<div style="height: ${content.height || "30px"};"></div>\n`;
          break;
        case "social":
          html += `<div style="text-align: center; margin: 20px 0;">`;
          if (content.facebook) html += `<a href="${content.facebook}" style="margin: 0 8px;"><img src="https://cdn-icons-png.flaticon.com/24/733/733547.png" alt="Facebook" /></a>`;
          if (content.instagram) html += `<a href="${content.instagram}" style="margin: 0 8px;"><img src="https://cdn-icons-png.flaticon.com/24/733/733558.png" alt="Instagram" /></a>`;
          if (content.linkedin) html += `<a href="${content.linkedin}" style="margin: 0 8px;"><img src="https://cdn-icons-png.flaticon.com/24/733/733561.png" alt="LinkedIn" /></a>`;
          html += `</div>\n`;
          break;
        case "html":
          html += content.code || "";
          break;
        default:
          break;
      }
    });

    html += `
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;

    return html;
  };

  // Save template
  const handleSave = () => {
    if (!templateName) {
      alert("Veuillez donner un nom au template");
      return;
    }
    const htmlContent = generateHTML();
    onSave({
      name: templateName,
      subject: templateSubject,
      blocks: blocks,
      html: htmlContent,
    });
  };

  // Filter sections by category
  const filteredSections = sectionCategory === "all" 
    ? SECTIONS 
    : SECTIONS.filter(s => s.category === sectionCategory);

  const categories = [...new Set(SECTIONS.map(s => s.category))];

  return (
    <div className="fixed inset-0 bg-white z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-gray-50">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={onCancel}>
            <X className="w-4 h-4 mr-2" />
            Fermer
          </Button>
          <div className="h-6 w-px bg-gray-300" />
          <Input
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="Nom du template"
            className="w-64"
          />
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setShowPreview(!showPreview)}>
            <Eye className="w-4 h-4 mr-2" />
            {showPreview ? "Éditeur" : "Aperçu"}
          </Button>
          <Button onClick={handleSave} className="bg-primary hover:bg-primary-hover">
            <Save className="w-4 h-4 mr-2" />
            Enregistrer
          </Button>
        </div>
      </div>

      {/* Subject line */}
      <div className="p-4 border-b bg-white">
        <div className="max-w-2xl mx-auto">
          <Label>Objet de l'email</Label>
          <Input
            value={templateSubject}
            onChange={(e) => setTemplateSubject(e.target.value)}
            placeholder="Objet de votre email..."
            className="mt-1"
          />
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar */}
        <div className="w-80 border-r bg-gray-50 overflow-y-auto">
          <Tabs value={sidebarTab} onValueChange={setSidebarTab}>
            <TabsList className="w-full grid grid-cols-3 p-1 m-2" style={{ width: "calc(100% - 16px)" }}>
              <TabsTrigger value="blocs">Blocs</TabsTrigger>
              <TabsTrigger value="sections">Sections</TabsTrigger>
              <TabsTrigger value="saved">Enregistré</TabsTrigger>
            </TabsList>

            <TabsContent value="blocs" className="p-2">
              <div className="grid grid-cols-3 gap-2">
                {BLOCK_TYPES.map((block) => (
                  <DraggableBlock
                    key={block.type}
                    type={block.type}
                    name={block.name}
                    icon={block.icon}
                  />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="sections" className="p-2">
              <div className="mb-4">
                <Select value={sectionCategory} onValueChange={setSectionCategory}>
                  <SelectTrigger>
                    <SelectValue placeholder="Catégorie" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes les sections</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-3">
                {filteredSections.map((section) => (
                  <SectionCard key={section.id} section={section} onAdd={addSection} />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="saved" className="p-4">
              <p className="text-sm text-muted-foreground text-center">
                Vos templates enregistrés apparaîtront ici
              </p>
            </TabsContent>
          </Tabs>
        </div>

        {/* Canvas */}
        <div className="flex-1 overflow-y-auto bg-gray-100 p-8">
          {showPreview ? (
            <div className="max-w-[600px] mx-auto bg-white rounded-lg shadow-lg overflow-hidden">
              <iframe title="Aperçu" sandbox="allow-same-origin" srcDoc={generateHTML()} style={{ width: "100%", border: 0, minHeight: 80 }} onLoad={(e) => { try { e.target.style.height = (e.target.contentDocument.documentElement.scrollHeight + 4) + "px"; } catch (_) {} }} />
            </div>
          ) : (
            <div
              className="max-w-[600px] mx-auto bg-white rounded-lg shadow-lg min-h-[500px] p-4"
              onDrop={handleCanvasDrop}
              onDragOver={handleDragOver}
            >
              {blocks.length === 0 ? (
                <div className="h-full min-h-[400px] flex flex-col items-center justify-center text-gray-400 border-2 border-dashed border-gray-200 rounded-lg">
                  <div className="text-center">
                    <Type className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium">Glissez des blocs ici</p>
                    <p className="text-sm mt-2">Commencez par ajouter des blocs depuis le panneau de gauche</p>
                  </div>
                </div>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={blocks.map((b) => b.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {blocks.map((block, index) => (
                      <SortableBlock
                        key={block.id}
                        block={block}
                        index={index}
                        onUpdate={updateBlock}
                        onDelete={deleteBlock}
                        isSelected={selectedBlockId === block.id}
                        onSelect={setSelectedBlockId}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              )}
            </div>
          )}
        </div>

        {/* Editor panel */}
        {selectedBlock && !showPreview && (
          <div className="w-80 border-l">
            <BlockEditor
              block={selectedBlock}
              onUpdate={updateBlock}
              onClose={() => setSelectedBlockId(null)}
            />
          </div>
        )}
      </div>
    </div>
  );
}
